"use client";

import { Plus } from "lucide-react";
import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Questions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [openItem, setOpenItem] = useState<string>("");
  const faqs = [{
    id: "item-1",
    ref: "FAQ-01",
    question: "How do I access my community water service account?",
    answer: "You can manage your account through our secure member portal. Please ensure you have your account number and service address ready. If you are a new resident, contact our office to initiate the transfer of service process."
  }, {
    id: "item-2",
    ref: "FAQ-02",
    question: "What is the process for reporting a water main break?",
    answer: "If you observe a leak or a suspected main break, please call our 24/7 emergency line immediately. Our operations team is dispatched based on the severity and location of the report to ensure minimal disruption to the community."
  }, {
    id: "item-3",
    ref: "FAQ-03",
    question: "How often is our local water quality tested?",
    answer: "We conduct rigorous water quality testing daily at our treatment facilities and weekly at various points throughout the distribution network. All results are compiled into an annual report available to all community members."
  }, {
    id: "item-4",
    ref: "FAQ-04",
    question: "Can I volunteer for the water committee?",
    answer: "Yes, we encourage community participation. Committee members are elected annually. If you are interested in serving, please review the committee bylaws and submit an application during the open nomination period each spring."
  }, {
    id: "item-5",
    ref: "FAQ-05",
    question: "What documentation is required for new service connections?",
    answer: "New connections require a completed service application, proof of property ownership or a valid lease agreement, and a site plan. Our office staff will guide you through the necessary permits and infrastructure requirements."
  }, {
    id: "item-6",
    ref: "FAQ-06",
    question: "How are water rates determined for the community?",
    answer: "As a nonprofit provider, our rates are set to cover operational costs, infrastructure maintenance, and long-term capital improvements. Any proposed rate adjustments are discussed in public board meetings before implementation."
  }, {
    id: "item-7",
    ref: "FAQ-07",
    question: "Are there programs for financial assistance?",
    answer: "We offer a hardship assistance program for community members facing temporary financial difficulties. Please contact our support office to discuss payment plans or eligibility for our community relief fund."
  }, {
    id: "item-8",
    ref: "FAQ-08",
    question: "How can I stay informed about service updates?",
    answer: "We provide regular updates through our community newsletter, the website news feed, and direct email alerts. You can sign up for notifications in the support section to receive timely information regarding maintenance or service changes."
  }];
  return <section data-section-id="10815" id="common-questions" className="bg-background text-foreground py-32 md:py-44">
      <div className="container mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={ref} className={`relative border-t border-foreground transition-all duration-500 ease-out ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="pt-4">
            <span className="text-[0.8125rem] font-medium uppercase tracking-[0.04em] text-foreground font-default">
              Support
            </span>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-6">
          {}
          <div className="md:col-span-4">
            <div className={`md:sticky md:top-[120px] transition-all duration-[420ms] ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}>
              <h2 className="font-default text-[clamp(2rem,3.4vw,3.5rem)] font-semibold leading-[1.04] tracking-[-0.02em] text-foreground">
                Community guidance.
              </h2>
              <p className="mt-6 md:mt-8 font-default text-[clamp(1.0625rem,1.1vw,1.1875rem)] font-normal leading-[1.55] text-muted-foreground">
                Find answers to common questions regarding your water service, committee involvement, and operational support. We are here to help our community members and staff navigate our services effectively.
              </p>
              <div className="mt-6 font-default text-[0.8125rem] tracking-[0.01em] text-muted-foreground">
                <div>{"support@waterserviceguide.org"}</div>
                <div className="mt-1">{"+1 (800) 555-WATER"}</div>
              </div>
            </div>
          </div>

          {}
          <div className={`md:col-span-8 md:col-start-5 transition-all duration-[420ms] ease-out delay-[120ms] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}>
            <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="w-full">
              {faqs.map((faq, i) => {
              const isOpen = openItem === faq.id;
              return <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-t border-b-0 border-border last:border-b last:border-b-foreground/20 first:border-t-foreground/20">
                    <AccordionTrigger className="group flex items-start justify-between gap-6 py-8 hover:no-underline [&>svg]:hidden focus-visible:ring-0 focus-visible:border-transparent">
                      <div className="flex flex-1 items-start gap-4 md:gap-6">
                        <span className="font-mono text-[0.8125rem] font-medium uppercase tracking-[0.02em] text-muted-foreground pt-1 shrink-0 hidden sm:inline">
                          {faq.ref}
                        </span>
                        <h4 className="font-default text-[clamp(1.125rem,1.4vw,1.375rem)] font-medium leading-[1.3] tracking-[-0.005em] text-foreground text-left transition-colors duration-200 group-hover:text-primary">
                          {faq.question}
                        </h4>
                      </div>
                      <Plus className={`size-5 shrink-0 mt-1 text-foreground transition-all duration-200 ease-out group-hover:text-primary ${isOpen ? "rotate-45" : "rotate-0"}`} strokeWidth={1} />
                    </AccordionTrigger>
                    <AccordionContent className="pb-8 pt-0">
                      <div className="flex gap-4 md:gap-6">
                        <span className="font-mono text-[0.8125rem] font-medium uppercase tracking-[0.02em] text-transparent shrink-0 hidden sm:inline select-none">
                          {faq.ref}
                        </span>
                        <p className="flex-1 font-default text-[0.9375rem] font-normal leading-[1.6] text-muted-foreground max-w-[64ch]">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>

            {}
            <div className="mt-10">
              <Link to="/SupportPage" className="inline-flex items-center gap-2 font-default text-[0.9375rem] font-medium text-primary underline underline-offset-[6px] decoration-primary hover:decoration-foreground transition-colors duration-200">
                <span>View full support resources</span>
                <span aria-hidden="true">{"->"}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}